<template>
  <div class="video-page space-y-3 w-full">
    <iframe
      :src="src"
      class="bg-black rounded-lg cursor-pointer"
      width="100%"
      height="550"
    />
    <div
      class="actions flex flex-col-reverse gap-3 md:flex-row justify-between mb-3"
    >
      <h2 class="text-xl" v-text="context.name" />
      <div class="space-x-4">
        <Button
          v-if="authStore.user"
          size="xs"
          :type="
            context?.reactions?.personal.includes('lovely') ? 'green' : 'gray'
          "
          :icon="IconHeart"
          :title="context?.reactions?.global?.lovely"
          class="flex gap-2 text-sm"
          @click="sendReaction('lovely')"
        />
        <Button
          size="xs"
          type="gray"
          title="Поделиться"
          :icon="IconShare3"
          class="flex gap-2 text-sm"
          @click="openShareModal(href)"
        />
        <Button
          v-if="origin"
          size="xs"
          type="gray"
          title="Скачать"
          :icon="IconDownload"
          class="flex gap-2 text-sm"
          @click="downloadFile(origin)"
        />
      </div>
    </div>
    <div class="prose-sm" :class="proseContent" v-html="context.description" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '@/store/auth.js'

import { IconDownload, IconHeart, IconShare3 } from '@tabler/icons-vue'

import ShareModal from '@/components/modals/ShareModal.vue'
import Button from '@/components/ui/UIButton.vue'
import useModalDynamic from '@/plugins/useModalDynamic.js'
import { useReactions } from '@/utils/reactions.js'
import { proseContent } from '@/utils/ui'

import { isObject } from '../../utils/isObject.js'
import { useFileDownloader } from '../../utils/use/useFileDownload.js'

interface Props {
  src: string
  context: Resource
}

type Resource = {
  id: number
  name: string
  description: string | null
  external_id: string | null
  poster: string | null
  preview: string | null
  reactions: {
    global: {
      wow: number
      lovely: number
      sad: number
      angry: number
    }
    personal: string[]
  }
  tags: string[]
  visibility: boolean
}

const href = ref('')
const props = defineProps<Props>()
const route = useRoute()
const authStore = useAuthStore()
const http = inject('http')
const { isDownloading, error, downloadFile } = useFileDownloader()

const queryKey = ['album', props.context.id]

const resource = ref(null)

const { setReaction } = useReactions({
  auth: authStore,
  queryKey,
  rootKey: 'album-videos',
  rootParamId: props.context.id,
})

const sendReaction = async (type: Reaction) => {
  if (props.context) {
    const path = `/api/albums/${route.params.albumId}/videos/${props.context.id}/react`
    setReaction(path, type, 'album-videos')
  }
}

const openShareModal = (link: string) => {
  useModalDynamic({
    opened: true,
    component: ShareModal,
    compAttrs: {
      context: {
        link,
      },
    },
  })
}

const handleMessage = (event: MessageEvent) => {
  if (event.origin !== 'https://video-cdn.gov-murman.ru') {
    return
  }

  const response = JSON.parse(event.data)
  if (response.type === 'resources') {
    resource.value = response.data
  }
}

function getFirstOrigin(resource) {
  if (!resource?.origins) return undefined
  return isObject(resource.origins)
    ? Object.values(resource.origins)[0]
    : resource.origins[0]
}

const origin = computed(() => getFirstOrigin(resource.value))

onMounted(async () => {
  window.addEventListener('message', handleMessage)
  setTimeout(() => (href.value = window.location.href), 100)
})

onUnmounted(() => {
  window.removeEventListener('message', handleMessage)
})
</script>
